// apps/dashboard/src/stores/notification.store.ts
// Zustand store for Web Push notification opt-in state.
// The actual subscription logic lives in usePushNotifications hook â€”
// this store tracks the enabled/disabled toggle state.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AppNotification {
  id: string;
  type:
    | 'ticket_assigned'
    | 'message_received'
    | 'system_alert'
    | 'success'
    | 'error'
    | 'conversion_detected'
    | 'sla_breached';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  data?: any;
}

interface NotificationState {
  pushEnabled: boolean;
  isToggling: boolean;
  setPushEnabled: (enabled: boolean) => void;
  setToggling: (toggling: boolean) => void;

  // In-app notifications
  notifications: AppNotification[];
  unreadCount: number;
  addNotification: (notification: Omit<AppNotification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      pushEnabled: false,
      isToggling: false,
      setPushEnabled: (enabled) => set({ pushEnabled: enabled }),
      setToggling: (toggling) => set({ isToggling: toggling }),

      notifications: [],
      unreadCount: 0,

      addNotification: (noti) => {
        const newNoti: AppNotification = {
          ...noti,
          id: Math.random().toString(36).substring(7),
          read: false,
          createdAt: new Date().toISOString(),
        };

        set((state) => ({
          notifications: [newNoti, ...state.notifications].slice(0, 50),
          unreadCount: state.unreadCount + 1,
        }));

        // Native Browser Notification
        if (Notification.permission === 'granted') {
          new Notification(newNoti.title, { body: newNoti.message });
        }
      },

      markAsRead: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        })),

      clearAll: () => set({ notifications: [], unreadCount: 0 }),
    }),
    {
      name: 'omniflow:notifications',
    },
  ),
);
